import type { PlayerProfile } from "../stats/profile";
import { CARD_THEMES } from "./themes";
import { evaluateUnlock, DECK_UNLOCKS, VENUE_UNLOCKS, type UnlockRule } from "./unlocks";
import { VENUES } from "./venues";
import { BADGES, badgeRim, FRAMES, RIMS, type Rim } from "./crests";

/**
 * What a round just OPENED — the decks and venues that were locked before it and
 * are not any more.
 *
 * The catalog already knows how to answer "is this unlocked?" for a profile
 * (`evaluateUnlock`), and that is all this needs: run the question over both
 * catalogs against the profile as it was before the round was banked and again
 * after, and the difference is the news. Nothing has to be remembered between
 * sessions and no unlock has to be taught to announce itself — a deck added to
 * `DECK_UNLOCKS` tomorrow is announced by this without being touched.
 *
 * It lives here rather than in unlocks.ts because it needs the NAMES, which
 * means themes.ts and venues.ts, and themes.ts already imports unlocks.ts.
 */
export interface CosmeticUnlock {
  kind: "deck" | "venue" | "frame" | "badge";
  id: string;
  name: string;
  /** A badge: the rim it now has. A badge is news again each time it climbs a rim. */
  rim?: Rim;
}

/**
 * The rules a round can move.
 *
 * Levels and achievements are things the player just did; a purchase is not, and
 * an entitlement arriving from the server mid-round would otherwise be announced
 * as though the round had earned it. Restricting the diff to the earned rules
 * also makes it a pure function of the profile, which is what lets the caller
 * take a before and an after and trust the subtraction.
 *
 * An event IS earned — by the round being recorded — and it reads nothing but
 * the profile's own record of having taken part, so it keeps the diff pure.
 */
function isEarnable(rule: UnlockRule | undefined): boolean {
  if (rule?.kind === "anyOf") return rule.rules.some(isEarnable);
  return rule?.kind === "level" || rule?.kind === "achievement" || rule?.kind === "event";
}

function unlockedKeys(profile: PlayerProfile): Set<string> {
  const keys = new Set<string>();
  for (const deck of CARD_THEMES) {
    const rule = DECK_UNLOCKS[deck.id];
    if (isEarnable(rule) && evaluateUnlock(rule, profile).unlocked) keys.add(`deck:${deck.id}`);
  }
  for (const venue of VENUES) {
    const rule = VENUE_UNLOCKS[venue.id];
    if (isEarnable(rule) && evaluateUnlock(rule, profile).unlocked) keys.add(`venue:${venue.id}`);
  }
  // Crest frames ride the same diff: a level-up that opens a frame is the same
  // kind of news as one that opens a deck. Badges are diffed by rim instead,
  // below, because a badge is not opened once — it climbs.
  for (const frame of FRAMES) {
    if (isEarnable(frame.rule) && evaluateUnlock(frame.rule, profile).unlocked) keys.add(`frame:${frame.id}`);
  }
  return keys;
}

/**
 * Everything unlocked in `after` that was locked in `before`: decks, then
 * venues, then crest frames, then crest badges that reached a new rim.
 *
 * Decks first because a deck is the thing the player is looking at for the whole
 * of the next round and a venue is the room around it — if a level opens both at
 * once, the cards are the better headline.
 */
export function newlyUnlockedCosmetics(before: PlayerProfile, after: PlayerProfile): CosmeticUnlock[] {
  const had = unlockedKeys(before);
  const has = unlockedKeys(after);
  const gained: CosmeticUnlock[] = [];
  for (const deck of CARD_THEMES) {
    const key = `deck:${deck.id}`;
    if (has.has(key) && !had.has(key)) gained.push({ kind: "deck", id: deck.id, name: deck.name });
  }
  for (const venue of VENUES) {
    const key = `venue:${venue.id}`;
    if (has.has(key) && !had.has(key)) gained.push({ kind: "venue", id: venue.id, name: venue.name });
  }
  for (const frame of FRAMES) {
    const key = `frame:${frame.id}`;
    if (has.has(key) && !had.has(key)) gained.push({ kind: "frame", id: frame.id, name: frame.name });
  }
  // One package per badge per round, at the best rim it reached: a round that
  // jumps a badge from nothing to gold hands over the gold one, not three boxes.
  for (const badge of BADGES) {
    const was = badgeRim(badge, before);
    const now = badgeRim(badge, after);
    if (now && (!was || RIMS.indexOf(now) > RIMS.indexOf(was))) {
      gained.push({ kind: "badge", id: badge.id, name: badge.name, rim: now });
    }
  }
  return gained;
}
